/**
 * FinanzenCockpitPage — Mandanten-Cockpit für die Finanzen.
 * Firmen-Umschalter: SV Büro / LCS / Immobilien / Steuer-ESt / Alle.
 */

import { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  FINANZEN_FIRMEN, getFinanzenFirma, getFinanzenSektionen,
  type FinanzenFirmaId,
} from '@/lib/navigation';
import FinanzenFirmaNav from '@/components/FinanzenFirmaNav';
import FinanzAssistentDrawer from '@/components/finanzen/FinanzAssistentDrawer';
import FinanzenDashboard from './FinanzenDashboard';
import BuchhaltungPage from './BuchhaltungPage';
import FahrzeugePage from './FahrzeugePage';
import TransaktionKlaerungPage from './TransaktionKlaerungPage';
import UeberweisungenPage from './UeberweisungenPage';
import ForderungenSeite from '@/components/forderungsmanager/ForderungenSeite';
import LiquiditaetPage from './LiquiditaetPage';
import KontoauszuegePage from './KontoauszuegePage';
import BwaPage from './BwaPage';
import FinanzAufgabenPage from './FinanzAufgabenPage';
import FinanzPruefungPage from './FinanzPruefungPage';
import FinanzSteuerberaterPage from './FinanzSteuerberaterPage';
import FinanzUstOrganschaftPage from './FinanzUstOrganschaftPage';
import ImmobilienBereich from './immobilien/ImmobilienBereich';
import EStBereich from './immobilien/EStBereich';
import {
  EingangsrechnungenTab, AusgangsrechnungenTab, DokumenteTab, BuchhaltungMonatTab,
  getBetriebConfig,
} from './BuchhaltungEinOrtPage';
import { Info, Building2, Sparkles } from 'lucide-react';

const DEFAULT_FIRMA: FinanzenFirmaId = 'sv_buero';

function FirmenuebergreifendHinweis({ firmaLabel }: { firmaLabel: string }) {
  return (
    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mb-4 flex items-start gap-2.5">
      <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="text-xs text-amber-700">
        Diese Ansicht ist aktuell firmenübergreifend — eine Filterung auf
        „{firmaLabel}“ folgt. Es werden alle Firmen angezeigt.
      </div>
    </div>
  );
}

function SektionInhalt({ firmaId, section }: { firmaId: FinanzenFirmaId; section: string }) {
  const firma = getFinanzenFirma(firmaId);
  const betriebKey = firma.betriebKey;
  const betrieb = betriebKey ? getBetriebConfig(betriebKey) : undefined;

  // ── Steuer / ESt: eigenes Modul ──
  if (firmaId === 'steuer') {
    return <EStBereich section={section} />;
  }

  // ── Privat: nur Bank-Cockpit (CSV-Upload mit Auto-Erkennung) ──
  if (firmaId === 'privat') {
    return <KontoauszuegePage firmaKey="privat" />;
  }

  // ── Immobilien: eigenes Modul (immo_* Tabellen) ──
  if (firmaId === 'immobilien') {
    if (section === 'kontoauszuege') {
      // Bank-Cockpit für die Ferienwohnung/Immobilien (CSV-Upload mit Auto-Erkennung).
      return <KontoauszuegePage firmaKey="immobilien" />;
    }
    if (section === 'liquiditaet') {
      return (
        <div>
          <FirmenuebergreifendHinweis firmaLabel={firma.label} />
          <LiquiditaetPage />
        </div>
      );
    }
    if (section === 'datev') {
      return (
        <div>
          <FirmenuebergreifendHinweis firmaLabel={firma.label} />
          <BuchhaltungPage embedded />
        </div>
      );
    }
    return <ImmobilienBereich section={section} />;
  }

  // ── SV Büro / LCS / Alle ──
  switch (section) {
    case 'uebersicht':
      return (
        <div>
          {firmaId !== 'alle' && <FirmenuebergreifendHinweis firmaLabel={firma.label} />}
          <FinanzenDashboard />
        </div>
      );

    case 'aufgaben':
      return <FinanzAufgabenPage firmaKey={(betriebKey as 'sv' | 'lcs' | 'alle') || 'sv'} />;

    case 'bwa':
      return <BwaPage firmaKey={(betriebKey as 'sv' | 'lcs' | 'alle') || 'sv'} />;

    case 'pruefung':
      return <FinanzPruefungPage firmaKey={(betriebKey as 'sv' | 'lcs' | 'alle') || 'sv'} />;

    case 'ust':
      return <FinanzUstOrganschaftPage firmaKey={(betriebKey as 'sv' | 'lcs' | 'alle') || 'alle'} />;

    case 'steuerberater':
      return <FinanzSteuerberaterPage firmaKey={(betriebKey as 'sv' | 'lcs' | 'alle') || 'sv'} />;

    case 'eingangsrechnungen':
      return <EingangsrechnungenTab firma={betrieb?.firma} />;

    case 'ausgangsrechnungen':
      return betrieb ? <AusgangsrechnungenTab betrieb={betrieb} /> : null;

    case 'buchhaltung':
      return <BuchhaltungMonatTab firma={betrieb?.firma} />;

    case 'dokumente':
      return (
        <div>
          {firmaId !== 'alle' && <FirmenuebergreifendHinweis firmaLabel={firma.label} />}
          <DokumenteTab />
        </div>
      );

    case 'fahrzeuge':
      return (
        <div>
          <FirmenuebergreifendHinweis firmaLabel={firma.label} />
          <FahrzeugePage />
        </div>
      );

    case 'kontoauszuege':
      if (betriebKey === 'sv' || betriebKey === 'lcs') {
        return <KontoauszuegePage firmaKey={betriebKey} />;
      }
      return (
        <div>
          <FirmenuebergreifendHinweis firmaLabel={firma.label} />
          <TransaktionKlaerungPage />
        </div>
      );

    case 'ueberweisungen':
      return (
        <div>
          <FirmenuebergreifendHinweis firmaLabel={firma.label} />
          <UeberweisungenPage />
        </div>
      );

    case 'forderungen':
      return <ForderungenSeite firma={(betriebKey as 'sv' | 'lcs' | 'alle') || 'sv'} embedded />;

    case 'liquiditaet':
      return (
        <div>
          {firmaId !== 'alle' && <FirmenuebergreifendHinweis firmaLabel={firma.label} />}
          <LiquiditaetPage />
        </div>
      );

    case 'datev':
      return <BuchhaltungPage embedded lockBusinessUnit={betrieb?.businessUnit} />;

    case 'konten':
      return <FinanzenDashboard />;

    default:
      return <FinanzenDashboard />;
  }
}

export default function FinanzenCockpitPage() {
  const [, setLocation] = useLocation();
  const [, params] = useRoute('/finanzen/:firma/:section?');
  const [assistentOffen, setAssistentOffen] = useState(false);

  const rawFirma = (params?.firma as FinanzenFirmaId) || DEFAULT_FIRMA;
  const firma = getFinanzenFirma(rawFirma);
  const firmaId = firma.id;
  const sektionen = getFinanzenSektionen(firmaId);
  const section = params?.section || sektionen[0]?.id || 'uebersicht';

  useEffect(() => {
    if (!params?.firma) {
      setLocation(`/finanzen/${DEFAULT_FIRMA}/${getFinanzenSektionen(DEFAULT_FIRMA)[0]?.id || 'uebersicht'}`, { replace: true });
    }
  }, [params?.firma, setLocation]);

  const wechsleFirma = (zielId: FinanzenFirmaId) => {
    const zielSektion = getFinanzenSektionen(zielId)[0]?.id || 'uebersicht';
    setLocation(`/finanzen/${zielId}/${zielSektion}`);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 lg:px-6 pt-5 pb-3 border-b border-gray-100 bg-white flex-shrink-0">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Finanzen</h1>
              <p className="text-xs text-gray-400">Mandanten-Cockpit — pro Firma an einem Ort</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setAssistentOffen(true)}
            data-testid="finanzen-assistent-button"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 flex-shrink-0"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Assistent</span>
          </button>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {FINANZEN_FIRMEN.map((f) => {
            const isActive = f.id === firmaId;
            return (
              <button
                key={f.id}
                onClick={() => wechsleFirma(f.id)}
                data-testid={`finanzen-firma-${f.id}`}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all border ${
                  isActive
                    ? 'text-white shadow-sm border-transparent'
                    : 'text-gray-600 bg-white border-gray-200 hover:bg-gray-50'
                }`}
                style={isActive ? { backgroundColor: f.farbe } : undefined}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: isActive ? 'rgba(255,255,255,0.85)' : f.farbe }}
                />
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="lg:hidden flex-shrink-0">
        <FinanzenFirmaNav firmaId={firmaId} section={section} mobile />
      </div>

      <div className="flex-1 flex min-h-0">
        <aside className="hidden lg:block w-[220px] flex-shrink-0 border-r border-gray-200">
          <FinanzenFirmaNav firmaId={firmaId} section={section} />
        </aside>

        <main className="flex-1 min-w-0 overflow-y-auto bg-gray-50/40">
          <div className="p-4 lg:p-6 max-w-[1400px] mx-auto">
            <SektionInhalt firmaId={firmaId} section={section} />
          </div>
        </main>
      </div>

      {/* Immer sichtbarer Floating-Action-Button — öffnet den Finanz-Assistenten
          auf allen Finanzen-Sektionen (liegt im Cockpit-Rahmen, fixed unten rechts). */}
      <button
        type="button"
        onClick={() => setAssistentOffen(true)}
        data-testid="finanzen-assistent-fab"
        aria-label="Finanz-Assistent öffnen"
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 pl-4 pr-5 h-12 rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-lg hover:bg-emerald-700 hover:shadow-xl transition-all"
      >
        <Sparkles className="w-5 h-5" />
        <span>Assistent</span>
      </button>

      <FinanzAssistentDrawer open={assistentOffen} onOpenChange={setAssistentOffen} />
    </div>
  );
}
